/**
 * Account API slice (profile, settings, memory, data export).
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { Platform } from 'react-native';
import { getClientInfo } from '@/constants/utils/analytics';
import { devLog } from '@/lib/devLog';
import { fetchWithAuth, ApiError, macosUploadFile, avatarUriToFile } from './client';
import { refreshToken } from './refresh';

export const accountApi = {
  getPersonalization: async () => {
    const data = await fetchWithAuth('/json/user/personalization');
    if (data.error || !data.personalization) throw new ApiError(data.message || 'Failed to load personalization', 400, data);
    return data.personalization;
  },
  updatePersonalization: async (preferences) => {
    const data = await fetchWithAuth('/json/user/personalization', { method: 'POST', body: preferences });
    if (data.error || !data.personalization) throw new ApiError(data.message || 'Failed to save personalization', 400, data);
    return data.personalization;
  },
  authorizeCheckoutUrl: async (url, purpose) => {
    const data = await fetchWithAuth('/json/payments/checkout-authorization', {
      method: 'POST', body: { purpose },
    });
    const token = data.checkoutAuthorization || data.data?.checkoutAuthorization;
    if (data.error || !token) throw new ApiError(data.message || 'Unable to authorize checkout', 403, data);
    const separator = url.indexOf('#');
    const base = separator < 0 ? url : url.slice(0, separator);
    const fragment = new URLSearchParams(separator < 0 ? '' : url.slice(separator + 1));
    fragment.set('checkoutAuthorization', token);
    return `${base}#${fragment.toString()}`;
  },
  getActivePurchase: async () => {
    const data = await fetchWithAuth('/json/account/active-purchase');
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch active purchase', 400, data);
    }
    
    return data.purchase;
  },

  getPurchaseHistory: async (page = 1) => {
    const data = await fetchWithAuth(`/json/account/purchase-history?page=${page}`);
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch purchase history', 400, data);
    }
    
    return {
      purchases: data.purchases || [],
      pagination: data.pagination || {}
    };
  },

  getReceipt: async (purchaseId) => {
    const data = await fetchWithAuth(`/json/account/purchases/${purchaseId}/receipt`);
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to get receipt', 400, data);
    }
    
    return data.receipt;
  },

  getUserCredits: async () => {
    try {
      const data = await fetchWithAuth('/json/user/credits');
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch user credits', 400, data);
      }
      
      return data.credits;
    } catch (error) {
      console.error('Error fetching user credits:', error);
      throw error;
    }
  },

  getUserInferenceCredits: async (page = 1, { startDate, endDate, pageSize, overview } = {}) => {
    try {
      let url = `/json/user/inference-credits?page=${page}`;
      if (pageSize) url += `&page_size=${encodeURIComponent(pageSize)}`;
      if (overview) url += '&overview=1';
      
      if (startDate) {
        url += `&start_date=${encodeURIComponent(startDate)}`;
      }
      if (endDate) {
        url += `&end_date=${encodeURIComponent(endDate)}`;
      }
      
      const data = await fetchWithAuth(url);
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch inference credit usages', 400, data);
      }

      const creditUsages = data.creditUsages?.data || [];
      const pagination = data.creditUsages?.pagination || data.pagination;

      return { creditUsages, pagination };
    } catch (error) {
      console.error('Error fetching inference credit usages:', error);
      throw error;
    }
  },

  getWorkUsageDetail: async taskId => {
    const data = await fetchWithAuth(`/json/user/usage-work-tasks/${encodeURIComponent(taskId)}`);
    if (data.error) throw new ApiError(data.message || 'Failed to load task costs', 400, data);
    return data.detail;
  },

  getCompletedWorkUsage: async (page = 1, { startDate, endDate }) => {
    const data = await fetchWithAuth(`/json/user/usage-work-tasks?page=${page}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`);
    if (data.error) throw new ApiError(data.message || 'Failed to load Work usage', 400, data);
    return { rows: data.rows || [], pagination: data.pagination };
  },

  getEarnPrograms: async () => {
    const data = await fetchWithAuth('/json/user/earn-programs');
    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch earn programs', 400, data);
    }
    return data.programs || [];
  },

  claimDailyCredits: async () => {
    const data = await fetchWithAuth('/json/user/daily-claim', { method: 'POST' });
    return data;
  },

  claimEarnProgram: async (programKey) => {
    const data = await fetchWithAuth('/json/user/earn-programs/claim', {
      method: 'POST',
      body: { programKey },
    });
    return data;
  },

  getMemories: async (collectionId) => {
    const qs = collectionId ? `?collectionId=${encodeURIComponent(collectionId)}` : '';
    const data = await fetchWithAuth(`/json/memory${qs}`);
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch memories', 400, data);
    }
    
    return data;
  },

  createMemory: async (content, collectionId) => {
    const data = await fetchWithAuth('/json/memory', {
      method: 'POST',
      body: { content, ...(collectionId ? { collectionId } : {}) }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to create memory', 400, data);
    }
    
    return data;
  },

  exportMemories: async () => {
    // Always pulls the JSON bundle (the text format is available server-side
    // for direct curl/wget users but fetchWithAuth expects JSON responses).
    // Callers can stringify to text for a .txt download.
    const data = await fetchWithAuth('/json/memory/export?format=json', {
      method: 'GET',
    });

    if (data?.error) {
      throw new ApiError(data.message || 'Failed to export memories', 400, data);
    }

    // Server returns the bundle directly, not wrapped in {data: ...}
    return data;
  },

  // Full account-data export. Two formats:
  //   - 'zip'  (default) — user-friendly ZIP archive with per-collection
  //     JSON files, individual conversation .md files, README + manifest.
  //     Returns { base64, contentType, filename } via a JSON transport that
  //     keeps the gateway connection alive while the archive is prepared.
  //   - 'json'           — single JSON bundle (parsed object).
  //
  // Excludes deleted records and temporary ("incognito") chats. Same shape
  // as `GET /v1/account/export`. Power users can hit /v1/ directly.
  exportAccountData: async ({ format = 'zip' } = {}) => {
    if (format !== 'zip' && format !== 'json') {
      throw new ApiError(`Invalid export format: ${format}`, 400);
    }
    const endpoint = `/json/account/export?format=${format}${format === 'zip' ? '&transport=json' : ''}`;
    // Bundles can be hefty for power users; bump the AbortController timeout
    // well past the default 30s.
    const opts = { method: 'GET', timeoutMs: 120_000, networkRetries: 0 };
    const data = await fetchWithAuth(endpoint, opts);

    if (data?.error) {
      throw new ApiError(data.message || 'Failed to export account data', 500, data);
    }
    if (format === 'zip' && (data?.contentType !== 'application/zip' || typeof data?.base64 !== 'string' || !data.base64)) {
      throw new ApiError('The server returned an invalid export archive. Please try again.', 502);
    }
    return data;
  },

  bulkCreateMemories: async (contents) => {
    const data = await fetchWithAuth('/json/memory/bulk', {
      method: 'POST',
      body: { contents }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to bulk create memories', 400, data);
    }

    // Backend returns: { success: true, message: "Success", data: { created: [...], skipped: [...] } }
    return data.data || data;
  },

  updateMemory: async (memoryId, content) => {
    const data = await fetchWithAuth(`/json/memory/${memoryId}`, {
      method: 'PUT',
      body: { content }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to update memory', 400, data);
    }
    
    return data;
  },

  deleteMemory: async (memoryId) => {
    const data = await fetchWithAuth(`/json/memory/${memoryId}`, {
      method: 'DELETE'
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete memory', 400, data);
    }
    
    return data;
  },

  updateMemoryPreferences: async (isEnabled, isAutoGenerationEnabled, autoSaveEnabled, dreamingEnabled) => {
    const body = {};
    if (isEnabled !== undefined) body.isEnabled = isEnabled;
    if (isAutoGenerationEnabled !== undefined) body.isAutoGenerationEnabled = isAutoGenerationEnabled;
    if (autoSaveEnabled !== undefined) body.autoSaveEnabled = autoSaveEnabled;
    if (dreamingEnabled !== undefined) body.dreamingEnabled = dreamingEnabled;

    const data = await fetchWithAuth('/json/user/memory-preferences', {
      method: 'POST',
      body
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to update memory preferences', 400, data);
    }

    return data;
  },

  updateMemoryProfile: async (profileSummary) => {
    const data = await fetchWithAuth('/json/user/memory-profile', {
      method: 'POST',
      body: { profileSummary: profileSummary ?? null },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to update memory overview', 400, data);
    }

    return data;
  },

  updateChatPreferences: async ({ followUpSuggestionsEnabled, collectionSuggestionsEnabled, stockChartsEnabled, enterToSend, workModeSuggestionsEnabled } = {}) => {
    const body = {};
    if (followUpSuggestionsEnabled !== undefined) {
      body.followUpSuggestionsEnabled = followUpSuggestionsEnabled;
    }
    if (collectionSuggestionsEnabled !== undefined) {
      body.collectionSuggestionsEnabled = collectionSuggestionsEnabled;
    }
    if (stockChartsEnabled !== undefined) {
      body.stockChartsEnabled = stockChartsEnabled;
    }
    if (enterToSend !== undefined) {
      body.enterToSend = enterToSend;
    }
    if (workModeSuggestionsEnabled !== undefined) {
      body.workModeSuggestionsEnabled = workModeSuggestionsEnabled;
    }

    const data = await fetchWithAuth('/json/user/chat-preferences', {
      method: 'POST',
      body,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to update chat preferences', 400, data);
    }

    return data;
  },

  updateAppearancePreferences: async ({ selectedPet, petSelectionCompleted, petPromoDismissed } = {}) => {
    const body = {};
    if (selectedPet !== undefined) {
      body.selectedPet = selectedPet;
    }
    if (petSelectionCompleted !== undefined) {
      body.petSelectionCompleted = petSelectionCompleted;
    }
    if (petPromoDismissed !== undefined) {
      body.petPromoDismissed = petPromoDismissed;
    }

    const data = await fetchWithAuth('/json/user/appearance-preferences', {
      method: 'POST',
      body,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to update appearance preferences', 400, data);
    }

    return data;
  },

  generatePetSpeech: async (conversationId) => {
    const data = await fetchWithAuth('/json/pets/speech', {
      method: 'POST',
      body: { conversationId },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to generate pet speech', 400, data);
    }

    return data.speech || null;
  },

  setDefaultModel: async (modelId, type) => {
    const data = await fetchWithAuth('/json/user/default-model', {
      method: 'POST',
      body: { modelId, type }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to set default model', 400, data);
    }
    
    return data;
  },

  setDefaultAgent: async (agentId) => {
    const data = await fetchWithAuth('/json/user/default-agent', {
      method: 'POST',
      body: { agentId },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to set default bot', 400, data);
    }
    return data;
  },

  cancelPlan: async (password, reason) => {
    const data = await fetchWithAuth('/json/account/cancel-plan', {
      method: 'POST',
      body: {
        password,
        reason: reason?.trim()
      }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to cancel subscription', 400, data);
    }
    
    return data;
  },

  createBtcPayInvoice: async ({ plan }) => {
    const data = await fetchWithAuth('/json/account/create-btcpay-invoice', {
      method: 'POST',
      body: { plan }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to create Bitcoin invoice', 400, data);
    }
    
    return data;
  },

  checkBtcPayInvoiceStatus: async (invoiceId) => {
    const data = await fetchWithAuth(`/json/account/btcpay-invoice/${invoiceId}/status`);
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to check invoice status', 400, data);
    }
    
    return data;
  },


  deleteAllConversations: async (password) => {
    const data = await fetchWithAuth('/json/account/delete-conversations', {
      method: 'POST',
      body: { password }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete conversations', 400, data);
    }
    
    return data;
  },

  archiveAllConversations: async (password) => {
    const data = await fetchWithAuth('/json/account/archive-all-conversations', {
      method: 'POST',
      body: { password }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to archive conversations', 400, data);
    }
    
    return data;
  },

  unarchiveAllConversations: async (password) => {
    const data = await fetchWithAuth('/json/account/unarchive-all-conversations', {
      method: 'POST',
      body: { password }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to unarchive conversations', 400, data);
    }
    
    return data;
  },

  deleteAllCollections: async (password) => {
    const data = await fetchWithAuth('/json/account/delete-all-collections', {
      method: 'POST',
      body: { password }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete collections', 400, data);
    }

    return data;
  },

  archiveAllCollections: async (password) => {
    const data = await fetchWithAuth('/json/account/archive-all-collections', {
      method: 'POST',
      body: { password }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to archive collections', 400, data);
    }

    return data;
  },

  unarchiveAllCollections: async (password) => {
    const data = await fetchWithAuth('/json/account/unarchive-all-collections', {
      method: 'POST',
      body: { password }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to unarchive collections', 400, data);
    }

    return data;
  },

  getUpdatePaymentMethodTransaction: async (subscriptionId) => {
    const data = await fetchWithAuth(`/json/paddle/subscription/${subscriptionId}/update-payment-method`);
    // devLog("getUpdatePaymentMethodTransaction", data);
    if (data.response && data.response.data && data.response.data.id) {
      return data.response.data.id;
    } else {
      throw new ApiError(data.message || 'Failed to get update payment method transaction', 400, data);
    }
    
  },

  // Initiate payment method update - returns URL for update iframe
  initiatePaymentMethodUpdate: async () => {
    const data = await fetchWithAuth('/json/account/update-payment-method', {
      method: 'POST'
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to initiate payment method update', 400, data);
    }
    
    return {
      updateUrl: data.updateUrl,
      planLength: data.planLength,
      customerId: data.customerId,
      paymentMethodId: data.paymentMethodId,
      purchaseId: data.purchaseId,
      isBounced: data.isBounced,
    };
  },

  extendSubscription: async (planLength) => {
    const data = await fetchWithAuth('/json/payments/extend-subscription', {
      method: 'POST',
      body: { planLength },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to extend subscription', data.status || 400, data);
    }

    return data;
  },

  deleteAccount: async (password, reason, deletionIdentity) => {
    const data = await fetchWithAuth('/json/account/delete', {
      method: 'POST',
      body: { password, reason, ...(deletionIdentity && { deletionIdentity }) }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete account', 400, data);
    }
    
    return data;
  },

  changeEmail: async (currentEmail, newEmail, password) => {
    const data = await fetchWithAuth('/json/account/email', {
      method: 'PUT', 
      body: {
        currentEmail,
        newEmail,
        password,
      }
    });
    
    if (data.error) {
      throw new ApiError(data.message || 'Failed to change email', 400, data);
    }
    
    return data;
  },

  changePassword: async (password, newPassword, newPassword2) => {
    const data = await fetchWithAuth('/json/auth/reset-password', {
      method: 'POST',
      body: {
        password,
        newPassword,
        newPassword2
      }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to change password', 400, data);
    }
    
    return data;
  },

  setPassword: async (newPassword, newPassword2) => {
    const data = await fetchWithAuth('/json/auth/set-password', {
      method: 'POST',
      body: {
        newPassword,
        newPassword2
      }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to set password', 400, data);
    }

    return data;
  },

  refreshToken,

  updateProfile: async (data) => {
    const formData = new FormData();
    
    if (data.fullName) formData.append('fullName', data.fullName);
    if (data.professionalRole) formData.append('professionalRole', data.professionalRole);
    if (data.removeAvatar) formData.append('removeAvatar', data.removeAvatar);
    if (Array.isArray(data.previousAiTools)) {
      data.previousAiTools.forEach(v => formData.append('previousAiTools', v));
    }
    if (Array.isArray(data.interestedFeatures)) {
      data.interestedFeatures.forEach(v => formData.append('interestedFeatures', v));
    }
    if (data.avatar) {
      const avatarType = data.avatar.type || 'image/png';
      const avatarName = data.avatar.name || 'avatar.png';
      if (Platform.OS === 'macos') {
        const otherFields = {};
        if (data.fullName) otherFields.fullName = data.fullName;
        if (data.professionalRole) otherFields.professionalRole = data.professionalRole;
        if (data.removeAvatar) otherFields.removeAvatar = data.removeAvatar;
        return macosUploadFile(data.avatar.uri, {
          fieldName: 'avatar',
          fileName: avatarName,
          mimeType: avatarType,
          endpoint: '/json/user/profile',
          extraFields: otherFields,
        });
      } else if (Platform.OS !== 'web') {
        formData.append('avatar', {
          uri: data.avatar.uri,
          type: avatarType,
          name: avatarName,
        });
      } else {
        try {
          // `expo-image-picker` on web returns the picked image as a `data:`
          // URL (FileReader.readAsDataURL). Some browsers/extensions fail
          // `fetch()` on data: URLs, so decode the data URL directly instead.
          const file = await avatarUriToFile(data.avatar.uri, avatarType, avatarName);
          formData.append('avatar', file);
        } catch (error) {
          console.error('Error creating avatar file:', error);
          throw new Error('Failed to process avatar image');
        }
      }
    }

    return fetchWithAuth('/json/user/profile', {
      method: 'POST',
      body: formData,
    });
  },
  
  // Cancellation save flow (docs/PLUS_CANCELLATION_SAVE.md §26/§30).
  // Ask the server what retention offer (if any) to show. Returns
  // { offer: null, gateReason } when the user is ineligible or no offer
  // applies; otherwise { offerId, expiresAt, offer: { label, offerKind,
  // percentage, durationCycles, tier } }.
  getSaveOffer: async ({ reason = null, reasonSecondary = null, reasonFreeText = null, channel = 'modal', step = 1, escalateFrom = null } = {}) => {
    const data = await fetchWithAuth('/json/account/cancel-plan/save-offer', {
      method: 'POST',
      body: { reason, reasonSecondary, reasonFreeText, channel, step, escalateFrom },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to load a save offer', 400, data);
    }

    return data;
  },

  // Redeem a minted offer. Idempotent on offerId. Returns
  // { applied: true, offerLabel, nextChargeDate, nextChargeAmount,
  // discountExpiresAt }.
  acceptSaveOffer: async (offerId) => {
    const data = await fetchWithAuth('/json/account/cancel-plan/save-offer/accept', {
      method: 'POST',
      body: { offerId },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to apply the offer', 400, data);
    }

    return data;
  },

  // Record that the user reached the offer and declined (analytics only).
  declineSaveOffer: async (offerId) => {
    try {
      await fetchWithAuth('/json/account/cancel-plan/save-offer/decline', {
        method: 'POST',
        body: { offerId },
      });
    } catch (_) {}
  },
};
